import React, { useState, useCallback, useMemo } from "react";
import MuiCheckbox from "@material-ui/core/Checkbox";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import ListSubheader from "@material-ui/core/ListSubheader";
import { makeStyles, Theme } from "@material-ui/core/styles";
import ExpandMore from "@material-ui/icons/ExpandMore";
import MUIAutocomplete, {
  AutocompleteRenderGroupParams,
  AutocompleteRenderInputParams,
} from "@material-ui/lab/Autocomplete";
import partition from "lodash/partition";
import { colorsLight, DropdownOption, SelectOptionsHook } from "@coherehealth/common";
import { TextField } from "@coherehealth/common";
import { Caption } from "@coherehealth/common";

// Module-scope no-op hook — called when useOptions prop is not provided so the
// hook call count in FilterDropdownWithSearch stays constant across renders.
const useNoOptions: SelectOptionsHook<DropdownOption> = () => ({
  options: [],
  optionsLoading: false,
  hasMoreOptions: false,
});

const SELECTED_GROUP = "Selected";
const UNSELECTED_GROUP = "Unselected";

interface FilterDropdownWithSearchProps {
  options: DropdownOption[];
  selectedValues: string[];
  onChange: (selectedIds: string[]) => void;
  label?: string;
  disabled?: boolean;
  /** When provided, data is fetched lazily (server-side search + scroll pagination)
   *  instead of using the static `options` list. */
  useOptions?: SelectOptionsHook<DropdownOption>;
}

const useStyles = makeStyles((theme: Theme) => {
  const colors = colorsLight;
  return {
    root: {
      "& .MuiAutocomplete-inputRoot": {
        paddingRight: `${theme.spacing(13)}px !important`,
      },
    },
    paper: {
      backgroundColor: colors.background.card,
      marginTop: 1,
      boxShadow: "0px 0px 1px rgba(0, 0, 0, 0.12), 0px 2px 1px rgba(0, 0, 0, 0.24)",
    },
    listbox: {
      padding: 0,
      maxHeight: 300,
    },
    option: {
      borderTop: `1px solid ${colors.gray.divider}`,
      padding: 0,
      "&:first-child": {
        borderTop: "none",
      },
      '&[aria-selected="true"]': {
        backgroundColor: "transparent",
      },
      '&[data-focus="true"]': {
        backgroundColor: colors.gray.light,
      },
    },
    optionContent: {
      display: "flex",
      alignItems: "center",
      width: "100%",
      padding: theme.spacing(1.5, 1.5),
      cursor: "pointer",
    },
    checkbox: {
      marginRight: theme.spacing(1),
      padding: 0,
    },
    optionLabel: {
      flex: 1,
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    groupLabel: {
      backgroundColor: colors.background.input,
      borderTop: `1px solid ${colors.gray.divider}`,
      padding: theme.spacing(1, 1.5),
      lineHeight: "normal",
      "&:first-of-type": {
        borderTop: "none",
      },
    },
    groupUl: {
      padding: 0,
    },
    endAdornment: {
      position: "absolute" as const,
      right: theme.spacing(1.5),
      top: "50%",
      transform: "translateY(-50%)",
    },
    chevronButton: {
      width: 40,
      height: 40,
      padding: 0,
      marginRight: -theme.spacing(1),
      "&:hover": {
        backgroundColor: "transparent",
      },
    },
    popupIndicator: {
      color: colors.font.tertiary,
      transition: "transform 200ms",
    },
    popupIndicatorOpen: {
      transform: "rotate(180deg)",
    },
  };
});

type GroupedOption = DropdownOption & { _group: string };

export default function FilterDropdownWithSearch({
  options,
  selectedValues,
  onChange,
  label,
  disabled = false,
  useOptions,
}: FilterDropdownWithSearchProps) {
  const [inputValue, setInputValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const classes = useStyles();

  // Always call a hook at this position — useNoOptions when not provided so the
  // hook call count stays constant and React's positional tracking is satisfied.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const {
    options: lazyOptions = [],
    optionsLoading,
    refetch,
    hasMoreOptions,
  } = (useOptions || useNoOptions)({
    query: inputValue,
    offset: 0,
    max: 10,
    onError: () => {},
  });

  // When lazy: loaded options + selected items that may not be in the current page
  // (ensures selected items are always visible in the "Selected" group).
  const effectiveOptions: DropdownOption[] = useMemo(() => {
    if (!useOptions) {
      return options;
    }
    const loadedIds = new Set(lazyOptions.map((o) => o.id));
    // Any previously selected item that hasn't been fetched in the current window
    // still needs to appear in the "Selected" group. For the assignee hook id === label
    // (both are the user's name), so we can reconstruct the DropdownOption from just the id.
    const selectedNotLoaded = selectedValues.filter((id) => !loadedIds.has(id)).map((id) => ({ id, label: id }));
    return [...selectedNotLoaded, ...lazyOptions];
  }, [useOptions, options, lazyOptions, selectedValues]);

  const isOptionSelected = useCallback(
    (option: DropdownOption) => selectedValues.includes(option.id),
    [selectedValues]
  );

  const groupedOptions: GroupedOption[] = useMemo(() => {
    const [selected, unselected] = partition(effectiveOptions, isOptionSelected);
    return [
      ...selected.map((opt) => ({ ...opt, _group: SELECTED_GROUP })),
      ...unselected.map((opt) => ({ ...opt, _group: UNSELECTED_GROUP })),
    ];
  }, [effectiveOptions, isOptionSelected]);

  // Client-side filter for static options only; lazy mode delegates search to the server.
  const filterOptions = useCallback(
    (opts: GroupedOption[], state: { inputValue: string }) => {
      if (useOptions) {
        return opts;
      }
      const query = state.inputValue.toLowerCase().trim();
      if (!query) {
        return opts;
      }
      return opts.filter((option) =>
        String(option.label || option.id)
          .toLowerCase()
          .includes(query)
      );
    },
    [useOptions]
  );

  // Scroll to bottom of listbox → load the next page.
  const handleListboxScroll = useCallback(
    (event: React.UIEvent<HTMLUListElement>) => {
      if (!refetch || !hasMoreOptions || optionsLoading) {
        return;
      }
      const el = event.currentTarget;
      // 50px threshold fires the next fetch before the user hits the very last pixel,
      // giving the request time to complete before they run out of visible items.
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 50) {
        refetch({ queryParams: { offset: lazyOptions.length, max: 10, query: inputValue } });
      }
    },
    [refetch, hasMoreOptions, optionsLoading, lazyOptions.length, inputValue]
  );

  const toggleOption = useCallback(
    (option: DropdownOption) => {
      if (selectedValues.includes(option.id)) {
        onChange(selectedValues.filter((id) => id !== option.id));
      } else {
        onChange([...selectedValues, option.id]);
      }
    },
    [selectedValues, onChange]
  );

  const displayLabel = useMemo(() => {
    if (selectedValues.length === 0) {
      return label;
    }
    return `${label} (${selectedValues.length} selected)`;
  }, [label, selectedValues.length]);

  const displayValue = useMemo(() => {
    if (selectedValues.length === 0) {
      return "";
    }
    return options
      .filter((opt) => selectedValues.includes(opt.id))
      .map((opt) => String(opt.label || opt.id))
      .join(", ");
  }, [selectedValues, options]);

  const renderOptionContent = useCallback(
    (option: GroupedOption) => (
      <div className={classes.optionContent}>
        <MuiCheckbox
          checked={isOptionSelected(option)}
          color="primary"
          className={classes.checkbox}
          disabled={option.disabled}
        />
        <span className={classes.optionLabel}>{option.label || option.id}</span>
      </div>
    ),
    [classes.optionContent, classes.checkbox, classes.optionLabel, isOptionSelected]
  );

  const renderGroup = useCallback(
    (params: AutocompleteRenderGroupParams) => {
      const childCount = React.Children.count(params.children);
      if (childCount === 0) {
        return null;
      }
      return (
        <li key={params.group}>
          <ListSubheader className={classes.groupLabel} component="div" disableSticky>
            <Caption color="textPrimary">
              {params.group} ({childCount})
            </Caption>
          </ListSubheader>
          <ul className={classes.groupUl}>{params.children}</ul>
        </li>
      );
    },
    [classes.groupLabel, classes.groupUl]
  );

  const handleChevronClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      if (!disabled) {
        setIsOpen((prev) => !prev);
      }
    },
    [disabled]
  );

  const renderInput = useCallback(
    (params: AutocompleteRenderInputParams) => {
      const shown = isOpen ? inputValue : displayValue;
      return (
        <TextField
          {...params}
          label={displayLabel}
          placeholder={isOpen ? "Type to search..." : undefined}
          disabled={disabled}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <InputAdornment position="end" className={classes.endAdornment}>
                <IconButton
                  className={classes.chevronButton}
                  onClick={handleChevronClick}
                  disabled={disabled}
                  tabIndex={-1}
                >
                  <ExpandMore className={`${classes.popupIndicator} ${isOpen ? classes.popupIndicatorOpen : ""}`} />
                </IconButton>
              </InputAdornment>
            ),
          }}
          inputProps={{
            ...params.inputProps,
            value: shown,
            style: {
              ...((params.inputProps as Record<string, unknown>)?.style as React.CSSProperties),
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            },
            onKeyDown: (e: React.KeyboardEvent) => {
              if (e.key === "Backspace" && !inputValue) {
                e.stopPropagation();
                return;
              }
              const original = (params.inputProps as { onKeyDown?: (e: React.KeyboardEvent) => void })?.onKeyDown;
              if (original) {
                original(e);
              }
            },
          }}
        />
      );
    },
    [
      isOpen,
      inputValue,
      displayValue,
      displayLabel,
      disabled,
      classes.endAdornment,
      classes.chevronButton,
      classes.popupIndicator,
      classes.popupIndicatorOpen,
      handleChevronClick,
    ]
  );

  return (
    <MUIAutocomplete
      multiple
      disableCloseOnSelect
      open={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={() => {
        setIsOpen(false);
        setInputValue("");
      }}
      options={groupedOptions}
      value={groupedOptions.filter((opt) => isOptionSelected(opt))}
      onChange={(_event, _value, reason, details) => {
        if (reason === "clear") {
          onChange([]);
          return;
        }
        if ((reason === "select-option" || reason === "remove-option") && details?.option) {
          toggleOption(details.option);
        }
      }}
      inputValue={inputValue}
      onInputChange={(_event, newValue, reason) => {
        if (reason === "input") {
          setInputValue(newValue);
        }
      }}
      filterOptions={filterOptions}
      groupBy={(option) => option._group}
      getOptionLabel={(option) => String(option.label || option.id)}
      getOptionSelected={(option, value) => option.id === value.id}
      getOptionDisabled={(option) => option.disabled || false}
      renderInput={renderInput}
      renderOption={renderOptionContent}
      renderGroup={renderGroup}
      renderTags={() => null}
      ListboxProps={{ onScroll: handleListboxScroll } as React.HTMLAttributes<HTMLUListElement>}
      disableClearable
      forcePopupIcon={false}
      disabled={disabled}
      classes={{
        root: classes.root,
        paper: classes.paper,
        listbox: classes.listbox,
        option: classes.option,
        groupLabel: classes.groupLabel,
        groupUl: classes.groupUl,
      }}
    />
  );
}
